import { useQuery } from '@tanstack/react-query';
import { Card } from '../../components/ui/Card.js';
import { useAbility } from '../../context/AbilityContext.js';
import { api } from '../../services/api.js';
import { countryName } from '../../utils/countries.js';
import { statusEventSummary } from './banReason.js';

interface UserEvent {
  _id: string;
  event_type: string;
  actor_type?: string;
  metadata?: Record<string, unknown>;
  created_at: string;
}

interface EventsResponse {
  items: UserEvent[];
}

// 036: explicit labels for the impersonation event types (others fall back to a
// humanised event_type). Keeps the timeline readable for the new audit entries.
const EVENT_LABELS: Record<string, string> = {
  impersonation_started: 'Impersonation started',
  impersonation_stopped: 'Impersonation ended',
  // 336 (#761): staff resent the verification link.
  verification_email_sent: 'Verification email sent',
};

function eventLabel(type: string): string {
  return EVENT_LABELS[type] ?? type.replace(/_/g, ' ');
}

/**
 * 330 (FR-010): a campus event says what it did — the names recorded at the time (so a later rename
 * or closure does not rewrite history), and the reason staff gave.
 */
function campusSummary(ev: UserEvent): { line: string; detail?: string; reason?: string } | null {
  const m = ev.metadata ?? {};
  const name = (v: unknown) => (typeof v === 'string' && v ? v : 'Not chosen');
  if (ev.event_type === 'campus_chosen') return { line: `Campus chosen: ${name(m.to_name)}` };
  if (ev.event_type === 'campus_changed') {
    return {
      line: `Campus changed: ${name(m.from_name)} → ${name(m.to_name)}`,
      reason: typeof m.reason === 'string' ? m.reason : undefined,
    };
  }
  return null;
}

/** 366: an IC number entry names what happened and the kind; the history never holds the number itself. */
function identitySummary(ev: UserEvent): { line: string; detail?: string; reason?: string } | null {
  const m = ev.metadata ?? {};
  const kind =
    m.kind === 'mykad'
      ? 'MyKad'
      : m.kind === 'passport'
        ? `Passport · ${countryName(String(m.country ?? ''))}`
        : null;
  const reason = typeof m.reason === 'string' ? m.reason : undefined;
  switch (ev.event_type) {
    case 'identity_number_given':
      return { line: kind ? `IC number given (${kind})` : 'IC number given' };
    case 'identity_number_revealed':
      return { line: 'IC number revealed' };
    case 'identity_number_changed':
      return { line: kind ? `IC number changed (${kind})` : 'IC number changed', reason };
    case 'identity_number_cleared':
      return { line: 'IC number cleared', reason };
    // 367: the Supplier role was retired by migration; this membership became Host.
    case 'membership_role_changed': {
      const n = typeof m.cars_assigned === 'number' ? m.cars_assigned : 0;
      return {
        line: `Supplier role retired: now Host (${n} car${n === 1 ? '' : 's'} assigned)`,
      };
    }
    default:
      return null;
  }
}

/** 381 (#1271): a status change says what it did, and a ban says why (the penalty, the note). */
function statusSummary(ev: UserEvent): { line: string; detail?: string; reason?: string } | null {
  if (ev.event_type !== 'status_changed') return null;
  const s = statusEventSummary(ev.metadata ?? {});
  return { line: s.line, detail: s.detail, reason: s.note };
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString('en-MY', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** User audit timeline (035 US4), newest-first. Mirrors the booking event
 * timeline. Admin-only — renders nothing without can:manage User. */
export function UserEventTimeline({ userId }: { userId: string }) {
  const ability = useAbility();
  const canManage = ability.can('manage', 'User');

  const { data } = useQuery({
    queryKey: ['user-events', userId],
    queryFn: () => api.get<EventsResponse>(`/users/${userId}/events`),
    enabled: canManage,
  });

  if (!canManage) return null;

  const items = data?.items ?? [];

  return (
    <Card>
      <h2 className="text-sm font-semibold text-steel uppercase tracking-wide mb-3">
        Activity timeline
      </h2>
      {!items.length && <p className="text-sm text-muted">No events yet.</p>}
      <ol className="space-y-3">
        {items.map((ev) => {
          const campus = campusSummary(ev) ?? identitySummary(ev) ?? statusSummary(ev);
          return (
            <li
              key={ev._id}
              className="flex gap-3 text-sm"
              data-testid={campus ? 'user-event-campus' : undefined}
            >
              <span className="text-muted select-none">•</span>
              <div>
                <span className={campus ? 'font-medium' : 'font-medium capitalize'}>
                  {campus ? campus.line : eventLabel(ev.event_type)}
                </span>
                {ev.actor_type && <span className="text-muted ml-1">by {ev.actor_type}</span>}
                <span className="ml-2 text-muted text-xs">{formatDate(ev.created_at)}</span>
                {campus?.detail && <p className="mt-0.5 text-ink">{campus.detail}</p>}
                {campus?.reason && <p className="mt-0.5 text-steel">“{campus.reason}”</p>}
              </div>
            </li>
          );
        })}
      </ol>
    </Card>
  );
}
